import { logger } from './logger'

export type UxMetricTags = Record<string, string | number | boolean | null | undefined>

type MetricKey = string

type TimingEntry = {
  count: number
  totalMs: number
  minMs: number
  maxMs: number
}

type CounterEntry = {
  count: number
}

function stableStringifyTags(tags?: UxMetricTags): string {
  if (!tags) return ''
  const keys = Object.keys(tags).sort()
  const normalized: Record<string, string | number | boolean | null> = {}
  for (const key of keys) {
    const value = tags[key]
    if (value === undefined) continue
    normalized[key] = value === null ? null : value
  }
  return JSON.stringify(normalized)
}

function makeKey(name: string, tags?: UxMetricTags): MetricKey {
  const sig = stableStringifyTags(tags)
  return sig ? `${name}::${sig}` : name
}

class UxMetrics {
  private counters = new Map<MetricKey, CounterEntry>()
  private timings = new Map<MetricKey, TimingEntry>()

  increment(name: string, tags?: UxMetricTags, by = 1) {
    const key = makeKey(name, tags)
    const entry = this.counters.get(key) ?? { count: 0 }
    entry.count += by
    this.counters.set(key, entry)
    logger.debug('[uxMetrics] increment', name, tags ?? {}, entry.count)
  }

  recordTiming(name: string, durationMs: number, tags?: UxMetricTags) {
    const key = makeKey(name, tags)
    const entry = this.timings.get(key) ?? {
      count: 0,
      totalMs: 0,
      minMs: Number.POSITIVE_INFINITY,
      maxMs: 0,
    }

    entry.count += 1
    entry.totalMs += durationMs
    entry.minMs = Math.min(entry.minMs, durationMs)
    entry.maxMs = Math.max(entry.maxMs, durationMs)

    this.timings.set(key, entry)
    logger.debug('[uxMetrics] timing', name, Math.round(durationMs), 'ms', tags ?? {})
  }

  startTimer(name: string, tags?: UxMetricTags) {
    const start = performance.now()
    let ended = false

    return (endTags?: UxMetricTags) => {
      if (ended) return
      ended = true
      const durationMs = performance.now() - start
      this.recordTiming(name, durationMs, { ...(tags ?? {}), ...(endTags ?? {}) })
    }
  }

  snapshot() {
    const counters: Record<string, CounterEntry> = {}
    const timings: Record<string, TimingEntry & { avgMs: number }> = {}

    for (const [key, value] of this.counters.entries()) counters[key] = { ...value }

    for (const [key, value] of this.timings.entries()) {
      timings[key] = {
        ...value,
        minMs: value.minMs === Number.POSITIVE_INFINITY ? 0 : value.minMs,
        avgMs: value.count ? value.totalMs / value.count : 0,
      }
    }

    return { counters, timings }
  }

  reset() {
    this.counters.clear()
    this.timings.clear()
  }

  dumpToConsole() {
    const snap = this.snapshot()
    logger.info('[uxMetrics] snapshot', snap)
    return snap
  }
}

export const uxMetrics = new UxMetrics()

// Expose helpers for manual evaluation runs.
if (typeof window !== 'undefined') {
  ;(window as any).dumpUxMetrics = () => uxMetrics.dumpToConsole()
  ;(window as any).resetUxMetrics = () => {
    uxMetrics.reset()
    logger.info('[uxMetrics] reset')
  }
}
